import { useParams, Link } from "react-router-dom";
import { MapPin, ArrowRight, Phone, ChevronRight } from "lucide-react";
import { getLocationById, LOCATIONS, LOCATION_IMAGES } from "@/data/locations";
import { SERVICES } from "@/data/services";
import { BUSINESS } from "@/data/businessInfo";
import { SEO } from "@/components/SEO";
import { LocationMap } from "@/components/GoogleMap";
import { LeadCaptureForm } from "@/components/LeadCaptureForm";
import { ServiceCardsSection } from "@/components/SharedSections";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { motion } from "framer-motion";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

export default function LocationPage() {
  const { id } = useParams();
  const location = getLocationById(id);

  if (!location) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-bold text-[#401303] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Location Not Found</h1>
        <Link to="/service-areas" className="text-[#317E8C] font-semibold">Back to Service Areas</Link>
      </div>
    );
  }

  const INDEXED_LOCATIONS = ["port-st-lucie", "fort-pierce", "stuart", "vero-beach", "west-palm-beach"];
  const shouldIndex = INDEXED_LOCATIONS.includes(id);

  return (
    <div>
      <SEO title={`Electrician ${location.name} FL - Electrical Services | Southern Light Solutions`} description={location.metaDescription} path={`/service-areas/${id}`} noindex={!shouldIndex} />
      {/* Breadcrumb */}
      <div className="bg-[#FAFAFA] border-b border-[#6B3A1F]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 text-xs text-[#6B3A1F]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Link to="/" className="hover:text-[#BF8D30]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/service-areas" className="hover:text-[#BF8D30]">Service Areas</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#401303] font-semibold">{location.name}</span>
        </div>
      </div>

      {/* Full-width Hero banner */}
      <section className="relative min-h-[45vh] lg:min-h-[50vh] flex items-center overflow-hidden" data-testid="location-hero">
        <img src={LOCATION_IMAGES[location.id] || LOCATION_IMAGES["port-st-lucie"]} alt={`Electrician in ${location.name}, Florida - Southern Light Solutions`} className="absolute inset-0 w-full h-full object-cover object-center" fetchpriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#401303]/85 via-[#401303]/60 to-[#401303]/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16 w-full">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-[#BF8D30]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF8D30]" style={{ fontFamily: 'Outfit, sans-serif' }}>{location.county}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Electrician in {location.name}, FL
          </h1>
          <p className="text-white/80 text-base lg:text-lg max-w-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>Licensed residential and commercial electrical services for homeowners and businesses in {location.name} and {location.county}.</p>
        </div>
      </section>

      <section className="bg-[#FAFAFA] py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.5 }}>
                <div className="flex flex-wrap gap-3 mb-8">
                  <Link to="/book" className="inline-flex items-center gap-2 px-6 py-3 btn-gradient rounded-full text-sm hover:bg-[#D97904] hover:text-white transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="location-book-btn">
                    Schedule Service in {location.name} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#401303] text-[#401303] font-bold rounded-full text-sm" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="location-call-btn">
                    <Phone className="w-4 h-4" /> Call Now
                  </a>
                </div>
                <div className="prose max-w-none">
                  {location.content.split('\n\n').map((para, i) => (
                    <p key={i} className="text-[#6B3A1F] leading-relaxed mb-4 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>{para.trim()}</p>
                  ))}
                </div>

                {/* Services available */}
                <div className="mt-10">
                  <h2 className="text-xl font-bold text-[#401303] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Electrical Services Available in {location.name}</h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {SERVICES.map(s => (
                      <Link key={s.id} to={`/services/${s.residential.slug}`} className="flex items-center gap-2 px-4 py-3 bg-white rounded-xl border border-[#6B3A1F]/10 hover:border-[#BF8D30]/40 transition-colors duration-200 group" data-testid={`location-service-${s.id}`}>
                        <span className="text-sm font-semibold text-[#401303] group-hover:text-[#BF8D30] transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-[#6B3A1F] ml-auto" />
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Neighborhoods */}
                {location.neighborhoods?.length > 0 && (
                  <div className="mt-10">
                    <h2 className="text-xl font-bold text-[#401303] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Neighborhoods We Serve in {location.name}</h2>
                    <div className="flex flex-wrap gap-2">
                      {location.neighborhoods.map(n => (
                        <span key={n} className="px-3 py-1.5 bg-white rounded-full border border-[#6B3A1F]/10 text-xs font-medium text-[#6B3A1F]" style={{ fontFamily: 'Manrope, sans-serif' }}>{n}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bottom CTA */}
                <div className="mt-10 p-6 bg-[#BF8D30]/10 rounded-2xl border border-[#BF8D30]/20">
                  <h3 className="text-lg font-bold text-[#401303] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Schedule Your Electrical Service in {location.name} Today</h3>
                  <p className="text-sm text-[#6B3A1F] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Contact Southern Light Solutions for honest pricing and quality workmanship from a trusted local electrician.</p>
                  <Link to="/book" className="inline-flex items-center gap-2 px-6 py-3 btn-gradient rounded-full text-sm" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="location-bottom-book-btn">
                    Request a Free Estimate <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-40 space-y-6">
                {/* Lead Capture Form */}
                <LeadCaptureForm title={`Get an Estimate in ${location.name}`} subtitle="Fill out the form and we'll respond within one business day." compact={true} />

                {/* Nearby Areas */}
                {location.nearbyAreas?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-[#6B3A1F]/10 p-6">
                    <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#401303] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Nearby Service Areas</h3>
                    <div className="space-y-2">
                      {location.nearbyAreas.map(area => {
                        const areaLoc = LOCATIONS.find(l => l.name === area);
                        return areaLoc ? (
                          <Link key={area} to={`/service-areas/${areaLoc.id}`} className="flex items-center gap-2 px-3 py-2 text-sm text-[#6B3A1F] hover:text-[#317E8C] rounded-lg hover:bg-[#FAFAFA] transition-colors duration-200">
                            <MapPin className="w-3.5 h-3.5" /> {area}
                          </Link>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {/* Location Map */}
                <LocationMap locationId={id} locationName={location.name} className="h-[250px]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location FAQ */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF8D30] mb-3 block" style={{ fontFamily: 'Outfit, sans-serif' }}>FAQ</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Electrical Services in {location.name} FAQ</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {[
              { q: `Do you serve all neighborhoods in ${location.name}?`, a: `Yes, we provide electrical services throughout ${location.name} and ${location.county}. Whether you're in ${location.neighborhoods?.slice(0,3).join(', ') || 'any neighborhood'}, or any other area, we're happy to help.` },
              { q: `How do I get a free estimate in ${location.name}?`, a: `Call us at ${BUSINESS.phone}, fill out our online booking form, or use the AI chatbot on this page. We'll schedule a convenient time to assess your needs and provide a detailed quote.` },
              { q: `What electrical services do you offer in ${location.name}?`, a: `We offer all of our services in ${location.name} including EV charger installation, generator installation, panel upgrades, lighting, ceiling fans, code corrections, troubleshooting, and AV & display setup — for both residential and commercial properties.` },
              { q: `Are you licensed to work in ${location.county}?`, a: `Yes. Southern Light Solutions LLC holds Florida Electrical Contractor License EC13007447, which covers all counties in Florida including ${location.county}. We are fully insured and BBB Accredited.` },
            ].map((faq, i) => (
              <AccordionItem key={i} value={`loc-faq-${i}`} className="bg-[#FAFAFA] rounded-xl border border-[#6B3A1F]/10 px-5 overflow-hidden">
                <AccordionTrigger className="text-left text-sm font-bold text-[#401303] py-4 hover:text-[#BF8D30] hover:no-underline" style={{ fontFamily: 'Outfit, sans-serif' }}>{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-[#6B3A1F] leading-relaxed pb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <ServiceCardsSection title={`Electrical Services in ${location.name}`} />
    </div>
  );
}
